import { useEffect, useRef, useState } from 'react'
import { postDataAPI } from '../../../lib/api'
import type { SignupUser } from '../SignupPage'
import OnboardingShell from './OnboardingShell'
import TextField from '../../../components/ui/TextField'
import { OnboardingStep } from './steps'

type DiscoveredLinks = {
  spotify?: string
  instagram?: string
  tiktok?: string
  twitter?: string
  facebook?: string
  youtube?: string
  soundcloud?: string
  appleMusic?: string
  topSongs?: any[]
  topVideo?: any
  events?: any[]
  artistName?: string
  profileImage?: string
}

function extractSpotifyUrl(profile: any): string {
  for (const field of [profile?.spotify, profile?.spotifyUrl, profile?.url]) {
    if (typeof field === 'string' && field.includes('spotify.com')) return field
  }
  return ''
}

export default function Step03FindPresence({
  setStep,
  user,
  setUser,
}: {
  setStep: (n: number) => void
  user: SignupUser
  setUser: React.Dispatch<React.SetStateAction<SignupUser>>
}) {
  const [input, setInput] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [profileList, setProfileList] = useState<any[] | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isDropdownMounted, setIsDropdownMounted] = useState(false)
  const [linksFound, setLinksFound] = useState<number>(0)
  const [artistName, setArtistName] = useState<string>('')
  const [selectedProfile, setSelectedProfile] = useState<any | null>(null)

  const dropdownWrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const el = dropdownWrapRef.current
      if (!el) return
      if (e.target instanceof Node && !el.contains(e.target)) setIsDropdownOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  useEffect(() => {
    if (isDropdownOpen) {
      setIsDropdownMounted(true)
      return
    }
    const t = window.setTimeout(() => setIsDropdownMounted(false), 180)
    return () => window.clearTimeout(t)
  }, [isDropdownOpen])

  const onChange = (value: string) => {
    setInput(value)
    setError(null)
    if (selectedProfile) setSelectedProfile(null)
  }

  useEffect(() => {
    const trimmed = input.trim()
    if (trimmed.length < 3 || selectedProfile) {
      setProfileList(null)
      setIsDropdownOpen(false)
      return
    }
    const timer = window.setTimeout(async () => {
      try {
        const res = await postDataAPI('user/discoverLinksFromArtist', { artistName: trimmed })
        const list = res?.data?.response
        if (Array.isArray(list)) {
          setProfileList(list)
          setIsDropdownOpen(list.length > 0)
        }
      } catch {
        // ignore
      }
    }, 600)
    return () => window.clearTimeout(timer)
  }, [input, selectedProfile])

  const discover = async (spotifyUrl: string) => {
    setIsProcessing(true)
    setError(null)
    try {
      const res = await postDataAPI('user/discoverLinksWithAI', { spotifyUrl })
      if (res?.status !== 200) throw new Error(res?.message || 'Could not discover links')

      const links: DiscoveredLinks = res?.data?.response || {}
      const count =
        Object.entries(links).filter(([key, value]) => {
          if (['artistName', 'topSongs', 'events', 'profileImage', 'topVideo'].includes(key)) return false
          if (!value) return false
          if (typeof value === 'string') return value.trim().length > 0
          if (Array.isArray(value)) return value.length > 0
          return true
        }).length || 0

      setLinksFound(count)
      setArtistName(links.artistName || '')

      const updated: SignupUser = {
        ...user,
        name: user.name || links.artistName,
        dizeeArtistName: links.artistName || user.dizeeArtistName,
        avatar: links.profileImage || user.avatar || null,
        topSongs: links.topSongs || [],
        topVideo: links.topVideo || {},
        social: {
          ...(user.social || {}),
          sf: links.spotify || (user.social || {})?.sf || '',
          ig: links.instagram || (user.social || {})?.ig || '',
          tt: links.tiktok || (user.social || {})?.tt || '',
          x: links.twitter || (user.social || {})?.x || '',
          fb: links.facebook || (user.social || {})?.fb || '',
          yt: links.youtube || (user.social || {})?.yt || '',
          sc: links.soundcloud || (user.social || {})?.sc || '',
          am: links.appleMusic || (user.social || {})?.am || '',
        },
      }

      setUser(updated)

      if (links.artistName) {
        localStorage.setItem('pendingBandsintownImport', links.artistName)
        setUser((prev) => ({ ...prev, pendingBandsintownImport: links.artistName }))
      }

      setStep(OnboardingStep.Username)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to discover links')
    } finally {
      setIsProcessing(false)
    }
  }

  const applyProfileWithoutDiscovery = (profile: any) => {
    const name = profile?.artistName || profile?.name || ''
    const image = profile?.profileImage || profile?.avatar || profile?.image || null
    setUser((prev) => ({
      ...prev,
      name: prev.name || name,
      dizeeArtistName: name || prev.dizeeArtistName,
      avatar: image || prev.avatar || null,
    }))
    if (name) {
      localStorage.setItem('pendingBandsintownImport', name)
      setUser((prev) => ({ ...prev, pendingBandsintownImport: name }))
    }
  }

  const handleSelectProfile = (profile: any) => {
    const spotifyUrl = extractSpotifyUrl(profile)
    const name = profile?.artistName || profile?.name || 'Artist'
    setInput(name)
    setSelectedProfile(profile)
    setIsDropdownOpen(false)
    if (spotifyUrl) {
      discover(spotifyUrl)
    } else {
      applyProfileWithoutDiscovery(profile)
    }
  }

  const handleNext = () => {
    if (!selectedProfile) return
    const spotifyUrl = extractSpotifyUrl(selectedProfile)
    if (spotifyUrl) {
      discover(spotifyUrl)
    } else {
      applyProfileWithoutDiscovery(selectedProfile)
      setStep(OnboardingStep.Username)
    }
  }

  return (
    <OnboardingShell
      title="Let's find your online presence"
      subtitle={linksFound ? `Found ${linksFound} links for ${artistName || 'your artist'}` : undefined}
      footer="Search by artist name to discover your profiles and connect your online presence."
    >
      <div className="space-y-5">
        <div ref={dropdownWrapRef} className="relative">
          <TextField
            label="Artist Name"
            value={input}
            onChange={(v) => onChange(v)}
            onFocus={() => {
              if (!selectedProfile && (profileList?.length || 0) > 0) setIsDropdownOpen(true)
            }}
            placeholder="Search by artist name"
            error={error}
          />

          {!selectedProfile && isDropdownMounted && profileList?.length ? (
            <div className="ds-dropdown-panel absolute z-50 mt-2 w-full overflow-hidden" data-state={isDropdownOpen ? 'open' : 'closed'}>
              <div className="ds-surface overflow-hidden" style={{ boxShadow: 'var(--shadow-elev-1)' }}>
                <div className="max-h-56 overflow-y-auto">
                  {profileList.slice(0, 10).map((p: any, idx: number) => {
                    const name = p?.artistName || p?.name || 'Artist'
                    const image = p?.profileImage || p?.avatar || p?.image || p?.profilePicture || p?.artistImage || null
                    const initials = String(name)
                      .split(' ')
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((w: string) => w[0]?.toUpperCase())
                      .join('')
                    return (
                      <button
                        key={idx}
                        type="button"
                        className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                        onClick={() => handleSelectProfile(p)}
                      >
                        <div className="h-9 w-9 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
                          {image ? <img src={image} alt={name} className="h-full w-full object-cover" /> : <span className="text-xs font-medium text-gray-700">{initials || '??'}</span>}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-gray-900 truncate">{name}</div>
                          
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 pt-2">
          <div className="flex gap-4">
            <button type="button" onClick={() => setStep(OnboardingStep.VerifyOtp)} className="ds-btn ds-btn-secondary flex-1">
              Go back
            </button>
            <button type="button" disabled={!selectedProfile || isProcessing} onClick={handleNext} className="ds-btn ds-btn-primary flex-1">
              {isProcessing ? 'Finding…' : 'Next'}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setStep(OnboardingStep.Username)}
            className="ds-btn ds-btn-ghost text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Skip for now
          </button>
        </div>
      </div>
    </OnboardingShell>
  )
}

